import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';

import { CoreModule } from './core/core.module';
import { ShellComponent } from './shell/shell/shell.component';
import { ExpensesModule } from './accounting/expenses/expenses.module';
import { ACCOUNTS_REDIRECT_ROUTES } from './accounting/accounts/accounts-redirect.routes';
import { permissionGuard } from './shared/guards/permission.guard';
import { moduleRouteGuard } from './shared/guards/module-route.guard';
import { appModuleGuard } from './launchpad/app-module.guard';
import { AppHostComponent } from './integrations/app-host/app-host.component';
import { SignupComponent } from './public/signup/signup.component';
import { ProvisioningStatusComponent } from './public/provisioning-status/provisioning-status.component';
import { CHANGE_PASSWORD_PATH } from './core/change-password/change-password.route';

// Exported so the route shape can be inspected without instantiating the module. NOTE: no
// spec imports this today — doing so crashes the karma bundle via a pre-existing, unrelated
// circular barrel import reached through this file's eager ExpensesModule import (see the
// Task 6 fix-round report, Finding 4, for the reproduction and root cause).
export const routes: Routes = [
  {
    path: 'signup',
    component: SignupComponent
  },
  {
    path: 'provisioning',
    component: ProvisioningStatusComponent
  },
  {
    path: '',
    component: ShellComponent,
    children: [
      {
        // The launchpad IS the root (ZEY-2925): every signed-in user lands on dev.zeymo.com/ — the shell's
        // header, no sidebar (the shell hides the drawer here). Not menu-gated.
        path: '',
        pathMatch: 'full',
        canActivate: [MsalGuard],
        loadComponent: () => import('./launchpad/launchpad.component').then(m => m.LaunchpadComponent)
      },
      {
        // ZEY-3140: the emailed "set your password for the new sign-in" link. Menu-less, any signed-in user; the
        // shell hides its sidebar here. MsalGuard (or the first API call) signs in with redirectStartPage = this URL
        // and navigateToLoginRequestUrl brings the user back to it.
        path: CHANGE_PASSWORD_PATH,
        canActivate: [MsalGuard],
        loadComponent: () => import('./core/change-password/change-password.component').then(m => m.ChangePasswordComponent)
      },
      {
        path: '',
        canActivate: [MsalGuard],
        loadChildren: () => CoreModule
      },
      {
        path: 'contacts',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./contacts/contacts.module').then(m => m.ContactsModule),
        data: { path: 'contacts' }
      },
      {
        path: 'suppliers',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./contacts/contacts.module').then(m => m.ContactsModule),
        data: { isSupplier: true, path: 'suppliers' }
      },
      {
        path: 'customers',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./contacts/contacts.module').then(m => m.ContactsModule),
        data: { isCustomer: true, path: 'customers' }
      },
      {
        path: 'quality',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 143 },
        loadChildren: () => import('./integrations/quality-ui-iframe/quality-ui-iframe.module').then(m => m.QualityUiIframeModule)
      },
      {
        // Treasury micro-UI (treasury-ui). Menu items are seeded under the
        // Treasury parent (TenantConfig MenuItemReference 152), so 'treasury'
        // is MENU_GATED and access follows the tenant's permitted menu.
        path: 'treasury',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./integrations/treasury-ui-iframe/treasury-ui-iframe.module')
          .then(m => m.TreasuryUiIframeModule)
      },
      {
        // Accounting micro-UI (accounting-ui). Menu-less for now — the native
        // /journals, /accounts, /expenses and /cashier-desk screens still own the
        // menu items, so this route is reached by typing the URL until the menu
        // cutover lands (TenantConfig branch zey-1680-accounting-menu-cutover).
        path: 'accounting',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 1 },
        loadChildren: () => import('./integrations/accounting-ui-iframe/accounting-ui-iframe.module')
          .then(m => m.AccountingUiIframeModule)
      },
      {
        path: 'storekeeper',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./logistics/storekeeper/storekeeper.module').then(m => m.StorekeeperModule)
      },
      {
        // Chart of Accounts moved to accounting-ui (ZEY-2804); old links forward there.
        path: 'accounts',
        canActivate: [MsalGuard],
        children: ACCOUNTS_REDIRECT_ROUTES
      },
      {
        path: 'account-classifications',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./accounting/account-classifications/account-classifications.module').then(m => m.AccountClassificationsModule)
      },
      {
        path: 'taxes',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 29 },
        loadChildren: () => import('./configuration/taxes/taxes.module').then(m => m.TaxesModule)
      },
      {
        path: 'tags',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 29 },
        loadChildren: () =>
          import('./configuration/tags/tags.routes').then((m) => m.tagsRoutes)
      },
      {
        // ZEY-2777: Configuration → Recipient groups (Billing / Logistics ... per document).
        path: 'recipient-groups',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 29 },
        loadChildren: () =>
          import('./configuration/recipient-groups/recipient-groups.routes').then(
            (m) => m.recipientGroupsRoutes
          )
      },
      {
        path: 'payment-terms',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 29 },
        loadChildren: () => import('./configuration/payment-terms/payment-terms.module').then(m => m.PaymentTermsModule)
      },
      {
        // Journal Vouchers moved to accounting-ui (ZEY-2809); the module's routes now only forward
        // old links there.
        path: 'journals',
        canActivate: [MsalGuard],
        loadChildren: () => import('./accounting/journals/journals.module').then(m => m.JournalsModule)
      },
      {
        path: 'currencies',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./configuration/currencies/currencies.module').then(m => m.CurrenciesModule)
      },
      {
        path: 'projects',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./integrations/projects-iframe/projects-iframe.module').then(m => m.ProjectsIframeModule)
      },
      {
        path: 'procurement',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 5 },
        loadChildren: () => import('./integrations/procurement-iframe/procurement-iframe.module').then(m => m.ProcurementIframeModule)
      },
      {
        path: 'quotes',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./sales/quotations/quotations.module').then(m => m.QuotationsModule)
      },
      {
        path: 'products',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./inventory/products/products.module').then(m => m.ProductsModule)
      },
      {
        path: 'product-categories',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 14 },
        loadChildren: () => import('./inventory/product-categories/product-categories.module').then(m => m.ProductCategoriesModule)
      },
      {
        path: 'products/dashboard',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 14 },
        loadChildren: () => import('./inventory/dashboard/inventory-dashboard.module').then(m => m.InventoryDashboardModule)
      },
      {
        path: 'warehouses',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./inventory/warehouses/warehouses.module').then(m => m.WarehousesModule)
      },
      {
        path: 'stock-audit',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./inventory/stock-audit/stock-audit.module').then(m => m.StockAuditModule)
      },
      {
        path: 'stock-transfers',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./inventory/transfer-orders/transfer-orders.module').then(m => m.TransferOrdersModule)
      },
      {
        path: 'manufacturing-orders',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./manufacturing/manufacturing-orders/manufacturing-order.module').then(m => m.ManufacturingOrderModule)
      },
      {
        path: 'new-manufacturing-orders',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./manufacturing/new-manufacturing-orders/new-manufacturing-order.module').then(m => m.NewManufacturingOrderModule)
      },
      {
        path: 'scheduling',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 17 },
        loadChildren: () => import('./manufacturing/scheduling/manufacturing-orders-scheduling.module').then(m => m.ManufacturingOrderSchedulingModule)
      },
      {
        path: 'bill-of-materials',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./manufacturing/bill-of-materials/bill-of-materials.module').then(m => m.BillOfMaterialsModule)
      },
      {
        path: 'production-routings',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./manufacturing/production-routings/production-routings.module').then(m => m.ProductionRoutingsModule)
      },
      {
        path: 'units-of-measure',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 29 },
        loadChildren: () => import('./configuration/unit-of-measure/unit-of-measure.module').then(m => m.UnitOfMeasureModule)
      },
      {
        path: 'sales/dashboard',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 9 },
        loadChildren: () => import('./sales/dashboard/sales-dashboard.module').then(m => m.SalesDashboardModule)
      },
      {
        path: 'sales/sales-reps',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 9 },
        loadChildren: () => import('./sales/sales-reps/sales-reps.module').then(m => m.SalesRepsModule)
      },
      {
        path: 'sales-orders',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./sales/sales-orders/sales-orders.module').then(m => m.SalesOrdersModule)
      },
      {
        path: 'sales-invoices',
        canActivate: [MsalGuard, permissionGuard],
        data: { parentPath: 'sales-invoices' },
        loadChildren: () => import('./sales/sales-invoice/sales-invoice.module').then(m => m.SalesInvoiceModule)
      },
      {
        path: 'credit-notes',
        canActivate: [MsalGuard, permissionGuard],
        data: { parentPath: 'credit-notes', moduleParentId: 9 },
        loadChildren: () => import('./sales/sales-invoice/sales-invoice.module').then(m => m.SalesInvoiceModule)
      },
      {
        path: 'pos/dashboard',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 59 },
        loadChildren: () => import('./sales/pos/pos.module').then(m => m.PosModule)
      },
      {
        path: 'new-sale',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./sales/pos/pos-sale/pos-sale.module').then(m => m.PosSaleModule)
      },
      {
        path: 'price-list',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 9 },
        loadChildren: () => import('./sales/product-prices/product-prices.module').then(m => m.ProductPricesModule)
      },
      {
        path: 'shops',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./sales/pos/shops/shops.module').then(m => m.ShopsModule)
      },
      {
        path: 'payment-methods',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 59 },
        loadChildren: () => import('./sales/pos/payment-methods/payment-methods.module').then(m => m.PaymentMethodsModule)
      },
      {
        path: 'purchase-invoices',
        canActivate: [MsalGuard, permissionGuard],
        data: { parentPath: 'purchase-invoices' },
        loadChildren: () => import('./purchasing/purchase-invoice/purchase-invoice.module').then(m => m.PurchaseInvoiceModule)
      },
      {
        path: 'debit-notes',
        canActivate: [MsalGuard, permissionGuard],
        data: { parentPath: 'debit-notes', moduleParentId: 5 },
        loadChildren: () => import('./purchasing/purchase-invoice/purchase-invoice.module').then(m => m.PurchaseInvoiceModule)
      },
      {
        path: 'purchase-orders',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./purchasing/purchase-order/purchase-order.module').then(m => m.PurchaseOrderModule)
      },
      {
        path: 'general-settings',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./configuration/settings/settings.module').then(m => m.SettingsModule)
      },
      {
        // Expenses moved to accounting-ui (ZEY-2808); ExpensesModule's routes now only forward old
        // links there. Keep the eager ExpensesModule import: the PI view's add/link-expense drawers
        // use its components.
        path: 'expenses',
        canActivate: [MsalGuard],
        loadChildren: () => ExpensesModule
      },
      {
        // Payments moved to accounting-ui (ZEY-2811); /payments and /issue-payments only forward
        // old links there.
        path: 'payments',
        canActivate: [MsalGuard],
        loadChildren: () => import('./accounting/payments/payments.module').then(m => m.PaymentsModule)
      },
      {
        path: 'issue-payments',
        canActivate: [MsalGuard],
        data: { moduleParentId: 1 },
        loadChildren: () => import('./accounting/issue-payments/issue-payments.module').then(m => m.IssuePaymentsModule)
      },
      {
        // Receipts moved to accounting-ui (ZEY-2810); /receipts and /issue-receipts only forward
        // old links there.
        path: 'receipts',
        canActivate: [MsalGuard],
        loadChildren: () => import('./accounting/receipts/receipts.module').then(m => m.ReceiptsModule)
      },
      {
        path: 'assets',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 58 },
        loadChildren: () => import('./integrations/assets-ui-iframe/assets-ui-iframe.module').then(m => m.AssetsUiIframeModule)
      },
      {
        path: 'issue-receipts',
        canActivate: [MsalGuard],
        data: { moduleParentId: 1 },
        loadChildren: () => import('./accounting/issue-receipts/issue-receipts.module').then(m => m.IssueReceiptsModule)
      },
      {
        path: 'companies',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./configuration/company/company.module').then(m => m.CompanyModule)
      },
      {
        path: 'resource-groups',
        canActivate: [MsalGuard, permissionGuard],
        data: { parentPath: 'resource-groups', moduleParentId: 29 },
        loadChildren: () => import('./configuration/resources/resources.module').then(m => m.ResourcesModule)
      },
      {
        path: 'resources',
        canActivate: [MsalGuard, permissionGuard],
        data: { parentPath: 'resources', moduleParentId: 29 },
        loadChildren: () => import('./configuration/resources/resources.module').then(m => m.ResourcesModule)
      },
      {
        path: 'stock-movements',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./inventory/stock-movements/stock-movements.module').then(m => m.StockMovementsModule)
      },
      {
        // ZEY-3168: Consignment add-on (menu module `consignment`, off unless a tenant is entitled).
        path: 'consignment',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./inventory/consignment/consignment.module').then(m => m.ConsignmentModule)
      },
      {
        path: 'wastage-orders',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./inventory/wastage-orders/wastage-orders.module').then(m => m.WastageOrdersModule)
      },
      {
        path: 'special-operations',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./inventory/stock-transformations/stock-transformations.module').then(m => m.StockTransformationsModule)
      },
      {
        path: 'reporting',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 20 },
        loadChildren: () => import('./reporting/reporting.module').then(m => m.ReportingModule)
      },
      {
        path: 'zapier',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./integrations/zapier/zapier.module').then(m => m.ZapierModule)
      },
      {
        path: 'users',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./admin-panel/users/users.module').then(m => m.UsersModule)
      },
      {
        path: 'roles-permissions',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./admin-panel/roles/roles.module').then(m => m.RolesModule)
      },
      {
        // Admin Panel → Subscription (ZEY-3126): the tenant's Zeymo subscription statement.
        path: 'subscription',
        canActivate: [MsalGuard, permissionGuard],
        loadComponent: () =>
          import('./admin-panel/subscription/subscription.component').then(m => m.SubscriptionComponent)
      },
      {
        path: 'profile',
        canActivate: [MsalGuard],
        loadChildren: () => import('./profile/profile.module').then(m => m.ProfileModule)
      },
      {
        path: 'user-permissions-overrides',
        canActivate: [MsalGuard],
        loadChildren: () => import('./profile/profile.module').then(m => m.ProfileModule)
      },
      {
        path: 'recalculation',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 55 },
        loadChildren: () => import('./recalculate/recalculate.module').then(m => m.RecalculateModule)
      },
      {
        path: 'chat',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./integrations/chat-ui-iframe/chat-ui-iframe.module').then(m => m.ChatUiIframeModule)
      },
      {
        path: 'approvals',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 109 },
        loadChildren: () => import('./integrations/approvals-ui-iframe/approvals-ui-iframe.module').then(m => m.ApprovalsUiIframeModule)
      },
      {
        // Renamed to /messaging-test (quick-260512-puj). Old /signalr-test bookmarks
        // keep working via this redirect. Angular Router does NOT support fragment
        // redirects in `redirectTo`, so the user lands on the default tab (Push).
        path: 'signalr-test',
        redirectTo: 'messaging-test',
        pathMatch: 'full'
      },
      {
        // Unified /messaging-test admin page with Push + Email + SignalR tabs
        // (quick-260512-puj). Admin-by-obscurity via hidden URL — MsalGuard only,
        // no permissionGuard (matches original /signalr-test pattern).
        path: 'messaging-test',
        canActivate: [MsalGuard],
        loadChildren: () => import('./messaging-test/messaging-test.module').then(m => m.MessagingTestModule)
      },
      {
        path: 'fleet',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 46 },
        loadChildren: () => import('./integrations/fleet-ui-iframe/fleet-ui-iframe.module').then(m => m.FleetUiIframeModule)
      },
      {
        path: 'custom-jobs',
        canActivate: [MsalGuard, permissionGuard],
        loadChildren: () => import('./integrations/costing-ui-iframe/costing-ui-iframe.module').then(m => m.CostingUiIframeModule)
      },
      {
        path: 'budgets',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 87 },
        loadChildren: () => import('./integrations/budget-ui-iframe/budget-ui-iframe.module').then(m => m.BudgetUiIframeModule)
      },
      {
        path: 'manufacturing',
        canActivate: [MsalGuard, permissionGuard],
        data: { moduleParentId: 123 },
        loadChildren: () => import('./integrations/manufacturing-ui-iframe/manufacturing-ui-iframe.module')
          .then(m => m.ManufacturingUiIframeModule)
      },
      {
        // The launchpad moved to the root (ZEY-2925); old /launchpad links land there.
        path: 'launchpad',
        redirectTo: '/',
        pathMatch: 'full'
      },
      {
        // One URL per module (ZEY-2419). The guard always redirects to the module's first screen, inside
        // the shell, with the sidebar narrowed to that module — Angular screen or micro-UI alike.
        path: 'app/:key',
        canActivate: [MsalGuard, appModuleGuard],
        children: []
      },
      {
        // App-platform registry catch-all (spec §6.3): every module route not owned by a
        // static entry above (e.g. hr, and any future registry-only module) is served by
        // the generic AppHostComponent, gated by moduleRouteGuard. MUST stay last — Angular
        // matches routes in array order and this consumes any remaining path. The guard
        // itself resolves which registry module (if any) owns the URL and redirects to
        // /unauthorized when there is no match or the user lacks access.
        path: '**',
        canActivate: [MsalGuard, moduleRouteGuard],
        component: AppHostComponent
      },
    ]
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload' })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
